import {
  AlternativeIcon,
  DeleteIcon,
  MarkNotAvailableIcon,
  SubstituteIcon,
  TotalDocumentsIcon,
} from 'components/common/icons';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { PERMISSIONS } from 'constants/permissions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalizeWords, isNil, toNumberOrMinusOne } from 'utils/helpers';
import { Button, Col, HasAccess, Row, Table, Tag, Typography } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { CommercialOffer, useCommercialOfferByIdQuery, useUpdateCommercialOfferMutation } from 'api';
import { CommercialOfferItem, CommercialOfferItemsTableProps } from './types';
import { AttachmentDrawer } from './../attachmentDrawer';
import { EditableCell } from './EditableCell';
import { EditableRow } from './EditableRow';
import { DeleteModal } from './../delete';
import styles from './styles.module.scss';
import './antd.scss';

const { Text, Title, Paragraph } = Typography;

type ItemType = 'SUBSTITUTE' | 'ALTERNATIVE' | 'NOT_AVAILABLE';
const calculateTotal = (dataSource: CommercialOfferItem[], key: keyof CommercialOfferItem) => {
  const result = dataSource.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  return result === 0 ? 0 : parseFloat(result.toFixed(2)).toString();
};

const ITEM_TYPE_MAPPING: Record<ItemType, JSX.Element> = {
  SUBSTITUTE: <SubstituteIcon />,
  ALTERNATIVE: <AlternativeIcon />,
  NOT_AVAILABLE: <MarkNotAvailableIcon color={'#FB3748'} />,
};

const columnsList = [
  { label: 'SKU', value: 'sku' },
  { label: 'Item Description', value: 'description' },
  { label: 'Qty', value: 'quantity' },
  { label: 'Price', value: 'pricePerPiece' },
  { label: 'Margin', value: 'margin' },
  { label: 'Discount', value: 'discount' },
  { label: 'Discount Price Per Piece', value: 'discountPricePerPiece' },
  { label: 'Lead Time', value: 'timeUnit' },
  { label: 'Attachments', value: 'attachments' },
  { label: 'Total', value: 'totalPurchasePrice' },
  { label: '', value: 'remove' },
];

export const CommercialOfferItemsTable: React.FC<CommercialOfferItemsTableProps> = ({
  originalForm,
  commercialOfferId,
  initialItems,
  subTable,
  subTableType,
  subTableBrand,
  subTableBrandId,
  subTableItemNumber,
  subTableSku,
  originalItemId,
  data: brandData,
}) => {
  const [editingRow, setEditingRow] = useState<string>('');
  const [editingColumn, setEditingColumn] = useState<string>('');
  const [deleteRecord, setDeleteRecord] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<CommercialOfferItem[]>([]);
  const [record, setRecord] = useState<Record<string, any> | undefined>();
  const [attachmentDrawerState, setAttachmentDrawerState] = useState<boolean>(false);
  const [drawerAttachments, setDrawerAttachments] = useState<Record<string, any>[] | undefined>();
  const { handleError, handleWarning } = useNotificationMessages();
  const { hasAccess } = useAccessControl();
  const { data: commercialOfferDetails = {} as CommercialOffer } = useCommercialOfferByIdQuery({
    id: commercialOfferId,
  });
  const [updateCommercialOfferItem] = useUpdateCommercialOfferMutation();
  const isViewOnly = !hasAccess(PERMISSIONS.deals.commercialOffers.write) || commercialOfferDetails?.isFinalStatus;

  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      const formattedItems = initialItems.map((item: any, index: number) => ({
        ...item,
        key: item.id || `${index}`,
      }));
      setDataSource(formattedItems);
    } else {
      setDataSource([]);
    }
  }, [initialItems]);

  const handleSave = async (row: CommercialOfferItem) => {
    try {
      await originalForm.validateFields();
      if (!row.sku || row.sku.trim() === '') {
        handleWarning('Sku is required!');
        return;
      }
      const payload = {
        sku: row.sku,
        notes: row.notes,
        description: row.description,
        timeUnit: row.timeUnit || undefined,
        quantity: isNil(row.quantity) ? undefined : toNumberOrMinusOne(row.quantity as string),
        margin: isNil(row.margin) ? undefined : toNumberOrMinusOne(row.margin as string),
        discount: isNil(row.discount) ? undefined : toNumberOrMinusOne(row.discount as string),
        leadTimeMin: isNil(row.leadTimeMin) ? undefined : toNumberOrMinusOne(row.leadTimeMin as string),
        leadTimeMax: isNil(row.leadTimeMax) ? undefined : toNumberOrMinusOne(row.leadTimeMax as string),
        pricePerPiece: isNil(row.pricePerPiece) ? undefined : toNumberOrMinusOne(row.pricePerPiece as string),
      };

      const { data, error } = await updateCommercialOfferItem(
        subTable
          ? {
              id: commercialOfferId,
              brands: [
                {
                  purchaseRequestId: brandData.purchaseRequestId,
                  substituteAlternativeItems: [
                    {
                      skuOriginalItem: originalItemId,
                      brandId: subTableBrandId,
                      items: [
                        {
                          ...payload,
                          sku: row.sku,
                        },
                      ],
                    },
                  ],
                },
              ],
            }
          : {
              id: commercialOfferId,
              brands: [
                {
                  purchaseRequestId: brandData.purchaseRequestId,
                  items: [
                    {
                      ...payload,
                      sku: row.sku,
                    },
                  ],
                },
              ],
            }
      );
      if (data?.success) {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => row.key === item.key);

        if (index > -1) {
          const item = newData[index];
          const updatedItem = {
            ...item,
            ...row,
            id: item.id,
          };
          newData.splice(index, 1, updatedItem);
          setDataSource(newData);
        }

        // Reset editing state
        setEditingRow('');
        setEditingColumn('');
      } else {
        handleError(error);
      }
    } catch (err: any) {
      setEditingRow('');
      setEditingColumn('');
      const error: any = {
        data: {
          success: false,
          data: {},
        },
      };
      err?.errorFields?.forEach((_error: any, index: string) => {
        error.data.data[index] = _error.errors[0];
      });
      handleError(error);
    }
  };

  const components = {
    body: {
      row: (props: any) => <EditableRow {...props} editingRow={editingRow} editingColumn={editingColumn} />,
      cell: EditableCell,
    },
  };

  const onAttachmentExpand = useCallback((value?: Record<string, any>[], record?: any) => {
    setRecord(record);
    setDrawerAttachments(value);
    setAttachmentDrawerState((oState) => !oState);
  }, []);

  const defaultColumns: any[] = useMemo(
    () => [
      {
        title: 'SKU',
        dataIndex: 'sku',
        editable: false,
        width: '148px',
        render: (text: string, record: any) => {
          return (
            <Row align={'middle'} gutter={7} wrap={false}>
              <Col flex={'auto'}>
                <Text
                  ellipsis={{
                    tooltip: true,
                  }}
                >
                  {text || '-'}
                </Text>
              </Col>
              {!subTable && (
                <Col>
                  {record.substituteCount && record.substituteCount > 0
                    ? ITEM_TYPE_MAPPING.SUBSTITUTE
                    : record.notAvailable
                      ? ITEM_TYPE_MAPPING.NOT_AVAILABLE
                      : ''}
                </Col>
              )}
            </Row>
          );
        },
      },
      {
        title: 'Item Description',
        dataIndex: 'notes',
        editable: !isViewOnly,
        width: '290px',
        render: (value: string, record: any) => (
          <Row>
            <Col span={24}>
              <Paragraph className={'reset-title'} ellipsis={{ rows: 2, tooltip: true }}>
                {record.description || '-'}
              </Paragraph>
            </Col>
            <Col span={24}>
              <Paragraph className={'reset-title'} ellipsis={{ rows: 2, tooltip: true }}>
                <Text className={'font-weight-700'}>Notes:</Text>
                {value || '-'}
              </Paragraph>
            </Col>
          </Row>
        ),
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        editable: false,
        width: '60px',
        render: (value: string) => (
          <Text
            className={'no-wrap'}
            ellipsis={{
              tooltip: true,
            }}
            style={{
              maxWidth: '50px',
            }}
          >
            {Number(value) || '-'}
          </Text>
        ),
      },
      {
        title: 'Price',
        dataIndex: 'pricePerPiece',
        editable: false,
        inputType: 'number',
        width: '100px',
        render: (value: number) => <Text className={'no-wrap'}>{Number(value) ? `${value}€` : '-'}</Text>,
      },
      {
        title: 'Margin',
        dataIndex: 'margin',
        editable: !isViewOnly,
        inputType: 'number',
        width: '100px',
        render: (value: number) => <Text className={'no-wrap'}> {Number(value) ? `${value}%` : '-'}</Text>,
      },
      {
        title: 'Discount',
        dataIndex: 'discount',
        editable: !isViewOnly,
        inputType: 'number',
        width: '100px',
        render: (value: number) => <Text className={'no-wrap'}> {Number(value) ? `${value}%` : '-'}</Text>,
      },
      {
        title: (
          <Text>
            Discount Price <br /> Per Piece
          </Text>
        ),
        dataIndex: 'discountPricePerPiece',
        editable: false,
        inputType: 'number',
        width: '120px',
        render: (value: number) => <Text className={'no-wrap'}> {Number(value) ? `${value}€` : '-'}</Text>,
      },
      {
        title: 'Lead Time',
        dataIndex: 'timeUnit',
        editable: !isViewOnly,
        width: '160px',
        render: (value: string, record: any) => {
          const { leadTimeMax, leadTimeMin } = record;
          return (
            <Row align={'middle'} gutter={4} wrap={false}>
              <Col flex={'auto'}>
                <Text type={'secondary'} className={'no-wrap'} ellipsis={{ tooltip: true }}>
                  {leadTimeMin && leadTimeMax
                    ? `${leadTimeMin}-${leadTimeMax}`
                    : leadTimeMin
                      ? `${leadTimeMin} Min`
                      : leadTimeMax
                        ? `${leadTimeMax} Max`
                        : '-'}
                </Text>
              </Col>
              {(leadTimeMin || leadTimeMax) && (
                <Col>
                  <Row className={'gap-4'} justify={'center'} wrap={false}>
                    <Tag className={`tag ${value === 'DAY' && 'selected-tag'}`}>Day</Tag>
                    <Tag className={`tag ${value === 'WEEK' && 'selected-tag'} `}>Week</Tag>
                  </Row>
                </Col>
              )}
            </Row>
          );
        },
      },
      {
        title: 'Attch.',
        dataIndex: 'attachments',
        editable: false,
        width: '70px',
        render: (value: Record<string, any>[], record: any) => {
          return value?.length ? (
            <Row wrap={false} align={'middle'}>
              <Col style={{ height: '18px' }}>
                <TotalDocumentsIcon />
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'text'}
                  variant={'text'}
                  style={{ maxHeight: '18px' }}
                  className={'underline-button'}
                  onClick={() => onAttachmentExpand(value, record)}
                >
                  <Text underline type={'secondary'}>
                    {value?.length}
                  </Text>
                </Button>
              </Col>
            </Row>
          ) : (
            <Text>-</Text>
          );
        },
      },
      {
        title: 'Total',
        dataIndex: 'totalPurchasePrice',
        fixed: 'right',
        width: '150px',
        render: (value: number) => <Text>{value ? `${value.toFixed(2)}€` : '-'}</Text>,
      },
    ],
    [isViewOnly, onAttachmentExpand, subTable]
  );

  if (hasAccess(PERMISSIONS.deals.commercialOffers.delete) && !defaultColumns.find((i) => i.dataIndex === 'remove')) {
    defaultColumns.push({
      fixed: 'right',
      dataIndex: 'remove',
      width: 34,
      key: 'remove',
      render: (value: string, record: any) => {
        return (
          <Row justify={'center'}>
            <HasAccess role={PERMISSIONS.deals.commercialOffers.delete}>
              <Button
                disabled={commercialOfferDetails?.isFinalStatus}
                onClick={() => {
                  setRecord(record);
                  setDeleteRecord(true);
                }}
                style={{ padding: 0, width: 16, height: 16 }}
                type={'text'}
                size={'small'}
                icon={<DeleteIcon />}
              />
            </HasAccess>
          </Row>
        );
      },
    });
  }

  const columns = defaultColumns.map((col) => {
    return {
      ...col,
      onCell: (record: CommercialOfferItem) => ({
        editable: col.editable,
        dataIndex: col.dataIndex,
        inputType: col.inputType,
        record,
        handleSave,
        className: ((record.substituteCount && record.substituteCount > 0) || record.notAvailable) && 'disabled-cell',
      }),
    };
  });
  return (
    <>
      <div>
        <Row justify="space-between" align="middle" className={styles.itemsHeaderRow}>
          <Col>
            {subTable ? (
              <Row align={'middle'} className={'gap-4'}>
                <Title level={5} style={{ color: '#05796B' }} className={'reset-title'}>
                  {capitalizeWords(subTableType || '')} for
                </Title>
                <Link to={`${ADMIN_ROUTES.items.view}/${subTableItemNumber}/suppliers`} target={'_blank'}>
                  <Title
                    level={5}
                    style={{
                      color: '#05796B',
                      fontWeight: 400,
                      cursor: 'pointer',
                    }}
                    className={'reset-title'}
                  >
                    &quot;{subTableSku}&quot;
                  </Title>
                </Link>
                <Title level={5} style={{ color: '#05796B' }} className={'reset-title'}>
                  item from
                </Title>
                <Link to={`${ADMIN_ROUTES.brand.view}/${subTableBrandId}/management`} target={'_blank'}>
                  <Title level={5} className={'reset-title'} style={{ color: '#05796B', cursor: 'pointer' }} underline>
                    {subTableBrand}
                  </Title>
                </Link>
              </Row>
            ) : (
              <Text style={{ fontSize: 18 }} strong>
                Items
              </Text>
            )}
          </Col>
        </Row>
        <div>
          <Table
            components={isViewOnly ? {} : components}
            bordered
            dataSource={dataSource}
            columns={columns as any}
            pagination={false}
            size="middle"
            className={styles.table}
            scroll={{ x: 850 }}
            summary={() =>
              subTable ? null : (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ backgroundColor: '#F5F7FA' }}>
                    {columnsList.map((col, index) => {
                      const total = calculateTotal(dataSource, col.value as keyof CommercialOfferItem);

                      // Custom rendering logic per column key
                      let content: React.ReactNode = '-';

                      if (col.value === 'remove') {
                        content = '';
                      } else if (index === 1) {
                        content = 'Total'; // second cell says "Total"
                      } else if (col.value === 'quantity' && total) {
                        content = total;
                      } else if (col.value === 'pricePerPiece' && total) {
                        content = `${total}€`;
                      } else if (col.value === 'totalPurchasePrice' && total) {
                        content = `${total}€`;
                      }

                      return (
                        <Table.Summary.Cell
                          key={col.value}
                          index={index}
                          className={
                            col.value === 'totalPurchasePrice'
                              ? 'table-last-total'
                              : col.value === 'remove'
                                ? 'fixed-columns'
                                : ''
                          }
                        >
                          {content}
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }
          />
        </div>
      </div>
      <AttachmentDrawer
        drawerState={attachmentDrawerState}
        onExpand={onAttachmentExpand}
        data={drawerAttachments}
        record={{
          commercialOfferId,
          purchaseRequestId: brandData.purchaseRequestId,
          itemType: subTable ? 'SUBSTITUTE_ALTERNATIVE' : 'ITEM',
          itemSku: record?.sku,
          brandId: subTable ? subTableBrandId : undefined,
          originalItemSku: subTable ? originalItemId : undefined,
        }}
      />
      <DeleteModal
        isSubTable={subTable}
        onDeleteModalClose={() => setDeleteRecord(false)}
        openModal={deleteRecord}
        record={{
          commercialOfferId,
          purchaseRequestId: brandData.purchaseRequestId,
          itemType: subTable ? 'SUBSTITUTE_ALTERNATIVE' : 'ITEM',
          itemSku: record?.sku,
          brandId: subTable ? subTableBrandId : undefined,
          originalItemSku: subTable ? originalItemId : undefined,
        }}
      />
    </>
  );
};
